// Admin — Urdu Translation Console.
//
// Two pages:
//   /admin/i18n/waves/:wid   → WaveUrduReview (side-by-side editor + toggle)
//   /admin/i18n/labels       → UrduLabels (the ~30 fixed UI strings)
//
// The wave-review page renders three columns per row: English (source,
// read-only), Urdu (editable), and a status pill (missing / draft / approved
// / stale). Each row has Approve / Save buttons; there's a Bulk-approve
// button up top and the master "Show Urdu version to patrons" toggle
// (blocked until 100% approved unless force is checked).
import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

// Tiny status pill component reused in the list + review.
function StatusPill({ status }) {
  const map = {
    missing:  { bg: "bg-neutral-200", fg: "text-neutral-700", label: "MISSING" },
    draft:    { bg: "bg-amber-100",   fg: "text-amber-800",   label: "AI DRAFT" },
    approved: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "APPROVED" },
    stale:    { bg: "bg-red-100",     fg: "text-red-800",     label: "STALE" },
  };
  const s = map[status] || map.missing;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wider ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

export function WaveUrduReview() {
  const { wid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({});           // key → { ur }
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/i18n/waves/${wid}/review`).then((r) => {
      setData(r.data);
      setEdits({});
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to load review"));
  }, [wid]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;

  const runBackfill = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/i18n/waves/${wid}/backfill`);
      toast.success(`Drafted ${r.data.translated} translation(s).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Backfill failed"); }
    finally { setBusy(false); }
  };

  const bulkApprove = async (only_status = null) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/i18n/waves/${wid}/bulk-approve`, only_status ? { only_status } : {});
      toast.success(`Approved ${r.data.approved} item(s).`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (nextVal) => {
    setBusy(true);
    try {
      await api.post(`/admin/i18n/waves/${wid}/toggle`, { show_urdu_version: nextVal });
      toast.success(nextVal ? "Urdu enabled for this wave." : "Urdu disabled.");
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Toggle failed";
      // Offer a "force" fallback so admin can enable Urdu even with drafts.
      if (msg.startsWith("Cannot enable Urdu") && nextVal) {
        if (window.confirm(`${msg}\n\nForce enable anyway (patrons will see un-approved AI drafts)?`)) {
          try {
            await api.post(`/admin/i18n/waves/${wid}/toggle`, { show_urdu_version: true, force: true });
            toast.success("Urdu enabled (forced).");
            load();
          } catch (e2) { toast.error(e2.response?.data?.detail || "Force-enable failed"); }
        }
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  const rowKey = (it) => {
    if (it.kind === "wave_field") return `wave:${it.field}`;
    if (it.kind === "question_text") return `q:${it.question_id}:text`;
    return `opt:${it.question_id}:${it.option_index}`;
  };

  const saveRow = async (it, { approve = true } = {}) => {
    const k = rowKey(it);
    const ur = edits[k]?.ur ?? it.ur ?? "";
    setSavingKey(k);
    try {
      if (it.kind === "wave_field") {
        await api.patch(`/admin/i18n/waves/${wid}`, { field: it.field, ur, approve });
      } else if (it.kind === "question_text") {
        await api.patch(`/admin/i18n/questions/${it.question_id}`, { ur, approve_text: approve });
      } else {
        await api.patch(`/admin/i18n/questions/${it.question_id}`, {
          options: [{ index: it.option_index, ur, approve }],
        });
      }
      toast.success(approve ? "Approved." : "Saved as draft.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingKey(null); }
  };

  const { totals, items, show_urdu_version, wave_name } = data;
  const approvedPct = totals.total ? Math.round(totals.approved / totals.total * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-8">
      <div className="mb-6">
        <button onClick={() => nav("/admin/waves")} className="text-xs text-neutral-500 hover:text-black mb-1">← Waves</button>
        <div className="overline text-neutral-500">URDU REVIEW · WAVE</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter">{wave_name}</h1>
      </div>

      {/* Master toggle + progress */}
      <div className="border-2 border-black bg-white p-5 mb-6" data-testid="wave-urdu-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-neutral-500 mb-1">MASTER TOGGLE</div>
            <div className="text-2xl font-black tracking-tight">
              Show Urdu version to patrons: <span className={show_urdu_version ? "text-emerald-700" : "text-neutral-500"}>{show_urdu_version ? "ON" : "OFF"}</span>
            </div>
            <p className="text-sm text-neutral-600 mt-1 max-w-xl">
              Urdu is only served to a patron when this toggle is ON <em>and</em> that patron
              picked اردو <em>and</em> the individual field is <span className="font-bold">approved</span>.
              Un-approved fields silently fall back to English.
            </p>
          </div>
          <Button
            onClick={() => toggle(!show_urdu_version)}
            disabled={busy}
            data-testid="wave-urdu-toggle"
            className={`rounded-none border-2 border-black px-6 ${show_urdu_version ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white text-black hover:bg-neutral-100"}`}
          >
            {show_urdu_version ? "Turn OFF" : "Turn ON →"}
          </Button>
        </div>

        <div className="mt-5">
          <div className="text-xs text-neutral-500 mb-1 flex items-center justify-between">
            <span><span className="font-bold text-emerald-700">{totals.approved}</span> approved · <span className="font-bold text-amber-700">{totals.draft}</span> draft · <span className="font-bold text-red-700">{totals.stale}</span> stale · <span className="font-bold text-neutral-500">{totals.missing}</span> missing · {totals.total} total</span>
            <span className="font-mono">{approvedPct}%</span>
          </div>
          <div className="h-2 bg-neutral-200 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${approvedPct}%` }} />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={runBackfill} disabled={busy} variant="outline" className="rounded-none" data-testid="wave-urdu-backfill">
            🪄 Draft missing (AI)
          </Button>
          <Button onClick={() => bulkApprove()} disabled={busy || totals.approved === totals.total} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none" data-testid="wave-urdu-bulk-approve">
            ✓ Approve all
          </Button>
          <Button onClick={() => bulkApprove("draft")} disabled={busy || totals.draft === 0} variant="outline" className="rounded-none" data-testid="wave-urdu-approve-drafts">
            Approve all AI drafts ({totals.draft})
          </Button>
        </div>
      </div>

      {/* Side-by-side list */}
      <div className="space-y-3">
        {items.map((it) => {
          const k = rowKey(it);
          const currentUr = edits[k]?.ur ?? it.ur ?? "";
          const dirty = edits[k] !== undefined && (edits[k].ur !== (it.ur || ""));
          const label = it.kind === "wave_field"
            ? `Wave · ${it.field.replace(/_/g, " ")}`
            : it.kind === "question_text"
              ? "Question stem"
              : `Option · ${it.option_key}`;
          return (
            <div key={k} className="border-2 border-black bg-white p-4" data-testid={`urdu-row-${k}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-neutral-500">{label}</div>
                <StatusPill status={it.status} />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] text-neutral-500 font-mono mb-1">ENGLISH (source)</div>
                  <div className="border border-neutral-200 bg-neutral-50 p-3 text-sm min-h-[3.5rem]">{it.en}</div>
                </div>
                <div>
                  <div className="text-[10px] text-neutral-500 font-mono mb-1">اردو (editable)</div>
                  <textarea
                    dir="rtl"
                    lang="ur"
                    rows={2}
                    value={currentUr}
                    onChange={(e) => setEdits({ ...edits, [k]: { ur: e.target.value } })}
                    className="w-full border border-neutral-300 p-3 text-base min-h-[3.5rem]"
                    style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif", lineHeight: 1.75 }}
                    data-testid={`urdu-input-${k}`}
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  disabled={savingKey === k}
                  onClick={() => saveRow(it, { approve: false })}
                  className="text-xs text-neutral-500 hover:text-black underline disabled:opacity-50"
                >
                  Save draft
                </button>
                <Button
                  size="sm"
                  disabled={savingKey === k || !currentUr.trim()}
                  onClick={() => saveRow(it, { approve: true })}
                  className={`rounded-none border-2 border-black text-xs ${it.status === "approved" && !dirty ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-black text-white hover:bg-neutral-800"}`}
                  data-testid={`urdu-approve-${k}`}
                >
                  {it.status === "approved" && !dirty ? "✓ Approved" : "Approve"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export function UrduLabels() {
  const [rows, setRows] = useState({});
  const [drafts, setDrafts] = useState({}); // key -> { en?, ur? } (only touched fields)
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/site-content/labels").then((r) => {
      setRows(r.data);
      setDrafts({});
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to load labels"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setDraft = (key, field, value) => {
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  };

  const save = async (key) => {
    setSaving(key);
    try {
      const en = drafts[key]?.en !== undefined ? drafts[key].en : rows[key]?.en;
      const ur = drafts[key]?.ur !== undefined ? drafts[key].ur : rows[key]?.ur;
      await api.put(`/admin/site-content/labels/${key}`, { en, ur });
      toast.success("Saved.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const keys = Object.keys(rows);
  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8">
      <div className="mb-6">
        <div className="overline text-neutral-500">SITE CONTENT · URDU</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter">Urdu labels</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
          Fixed UI strings that appear across the panelist app — button labels,
          empty states, and small words like "questions", "min", "pts". Both
          English and اردو are editable here now. English is shown to every
          patron by default (and is the fallback if Urdu is off or blank);
          Urdu is shown to any patron who picks اردو from the header. Changes
          are live immediately.
        </p>
      </div>

      <div className="border-2 border-black bg-white overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_120px] px-4 py-3 border-b-2 border-black bg-neutral-100 text-xs font-mono uppercase tracking-wider">
          <div>English</div>
          <div>اردو</div>
          <div></div>
        </div>
        {keys.map((k) => {
          const row = rows[k];
          const curEn = drafts[k]?.en !== undefined ? drafts[k].en : row.en;
          const curUr = drafts[k]?.ur !== undefined ? drafts[k].ur : row.ur;
          const dirty = (drafts[k]?.en !== undefined && drafts[k].en !== row.en) || (drafts[k]?.ur !== undefined && drafts[k].ur !== row.ur);
          return (
            <div key={k} className="grid grid-cols-[1fr_1fr_120px] items-start gap-2 px-4 py-3 border-b border-neutral-200" data-testid={`label-row-${k}`}>
              <div>
                <textarea
                  rows={2}
                  value={curEn || ""}
                  onChange={(e) => setDraft(k, "en", e.target.value)}
                  className="w-full border border-neutral-300 px-3 py-2 text-sm"
                  data-testid={`label-input-en-${k}`}
                />
                <div className="text-[10px] text-neutral-400 font-mono mt-1">{k}</div>
              </div>
              <textarea
                dir="rtl"
                lang="ur"
                rows={2}
                value={curUr || ""}
                onChange={(e) => setDraft(k, "ur", e.target.value)}
                className="w-full border border-neutral-300 px-3 py-2 text-base"
                style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif" }}
                data-testid={`label-input-${k}`}
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!dirty || saving === k}
                  onClick={() => save(k)}
                  className="rounded-none text-xs"
                  data-testid={`label-save-${k}`}
                >
                  {saving === k ? "…" : "Save"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
